import { t } from "@hakili/i18n";
import { Card } from "@hakili/ui";

import { accountLine, partnerStatus, DASH, ManagerBadge, StatusChip, type Partner } from "./partner-cards.js";

/**
 * `P0-CNW-029`: the dealership directory, from `GET /api/v1/organisations`.
 *
 * `round CN-B`, `CN-B.2`: **the drawing's own table** (`hakili-console.jsx`,
 * `ConcessionnairesDirectory`): the concession and its contact, the address
 * and the account line DERIVED from the accounts, then the reliability grade,
 * the financed files and the prepaid balance , each a dash until its module
 * answers (`catalog`'s grade, `lending`'s disbursements, `payments`' balance)
 * , the status chip, and the manager named under the administration. A row
 * opens the fiche, as the drawing's does.
 */

export type { Partner, PartnerAdministrator } from "./partner-cards.js";

export interface DealershipsProps {
  /** `null` while the door has not answered, so nothing flashes an empty list. */
  readonly partners: readonly Partner[] | null;
  readonly vue: string;
  /** Opens the fiche of one dealership. */
  readonly onOpen: (dealership: { id: string; nom: string }) => void;
}

export function Dealerships({ partners, vue, onOpen }: DealershipsProps): React.JSX.Element {
  if (partners === null) {
    return (
      <Card>
        <p className="text-sm text-gray-500">{t("common.state.chargement")}</p>
      </Card>
    );
  }

  const dealerships = partners.filter((partner) => partner.type === "concessionnaire");

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-center gap-4 border-b border-gray-100 px-5 py-2.5">
        <span className="w-48 text-xs text-gray-500">{t("console.dealerships.colConcession")}</span>
        <span className="flex-1 text-xs text-gray-500">{t("console.dealerships.colAdresse")}</span>
        <span className="w-20 text-right text-xs text-gray-500">{t("console.dealerships.colFiabilite")}</span>
        <span className="w-28 text-right text-xs text-gray-500">{t("console.dealerships.colFinances")}</span>
        <span className="w-28 text-right text-xs text-gray-500">{t("console.dealerships.colSolde")}</span>
        <span className="w-44 text-right text-xs text-gray-500">{t("console.dealerships.colStatut")}</span>
        {vue === "admin" ? (
          <span className="w-7 shrink-0 text-center text-xs text-gray-500">
            {t("console.dealerships.colSuivi")}
          </span>
        ) : null}
      </div>
      {dealerships.length === 0 ? (
        // The drawing's own sentence for an empty portfolio.
        <p className="px-5 py-6 text-sm leading-relaxed text-gray-500" data-testid="dealerships-empty">
          {t("console.dealerships.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {dealerships.map((dealership) => (
            <li
              key={dealership.id}
              className="flex cursor-pointer items-center gap-4 px-5 py-3.5 hover:bg-gray-50"
              data-testid={`dealership-${dealership.id}`}
              data-state={dealership.administrator?.state ?? "aucun"}
              onClick={() => {
                onOpen({ id: dealership.id, nom: dealership.name });
              }}
            >
              <div className="w-48 shrink-0">
                <p className="text-sm font-medium text-gray-900">{dealership.name}</p>
                <p className="truncate text-xs text-gray-500">
                  {dealership.administrator?.fullName ?? t("console.dealerships.noAdministrator")}
                </p>
                <p className="truncate text-xs text-gray-500">{dealership.administrator?.email ?? DASH}</p>
              </div>
              <div className="min-w-0 flex-1">
                {/* The address is `partnerships`' and read on the fiche; a list of
                    N addresses would be N reads for a column nobody scans. */}
                <p className="truncate text-sm text-gray-700">{DASH}</p>
                <p data-testid="dealership-comptes" className="truncate text-xs text-gray-500">
                  {accountLine(dealership)}
                </p>
              </div>
              <span className="w-20 shrink-0 text-right text-sm font-medium tabular-nums">{DASH}</span>
              <span className="w-28 shrink-0 text-right text-sm tabular-nums">{DASH}</span>
              <span className="w-28 shrink-0 text-right text-sm tabular-nums">{DASH}</span>
              <span className="flex w-44 shrink-0 justify-end">
                <StatusChip status={partnerStatus(dealership)} />
              </span>
              {vue === "admin" ? <ManagerBadge name={dealership.managerName} /> : null}
            </li>
          ))}
        </ul>
      )}
      <p className="px-5 py-3 text-sm leading-relaxed text-gray-500">{t("console.dealerships.note")}</p>
    </div>
  );
}
